import classNames from 'classnames';
import { ItemCard, Panel, useItemTransferContext } from '@sorrem-client/ui/components';
import {
    resolveCharacterHomeItemCard,
    resolveTradeEssence,
    useCharacterHomeCharacter,
    useCharacterHomeTrade,
} from '@sorrem-client/ui/features/CharacterHome/components/CharacterHomeItemTransfer';
import { TradeBar } from '../TradeBar';
import styles from './InventoryItemPanel.module.scss';

/**
 * The Inventory tab's side panel: the selected item's card, a carried or
 * stored one compared with what it would replace, and at the panel's foot,
 * trading it to a house of exchange for the essence it is worth. An equipped
 * item shows its card, but has to be taken off before it can be traded.
 */
export function InventoryItemPanel(): JSX.Element {
    const character = useCharacterHomeCharacter();
    const { selectedItemId, clearSelectedItem } = useItemTransferContext();
    const { isTrading, error, tradeItem } = useCharacterHomeTrade(character);

    const card = selectedItemId === null || character === null ? undefined : resolveCharacterHomeItemCard(character, selectedItemId);
    const item = card?.item;
    // Crystals are bound to a run and never reach Character Home, so only equipment trades.
    const essence = item === undefined ? null : resolveTradeEssence(item);

    const handleTrade = async (): Promise<void> => {
        if (item !== undefined && await tradeItem(item.id)) {
            clearSelectedItem();
        }
    };

    return (
        <Panel className={styles.inventoryItemPanel}>
            {card === undefined || item === undefined || essence === null ? (
                <p className={styles.emptyNote}>Select an item, carried, stored or equipped, to see its details.</p>
            ) : card.isEquipped ? (
                <>
                    <ItemCard item={item} />
                    <p className={classNames(styles.emptyNote, styles.foot)}>Take the item off to trade it.</p>
                </>
            ) : (
                <>
                    <ItemCard item={item} compared={card.compared} />
                    {error !== null && <p className={styles.error} role='alert'>{error}</p>}
                    {/* One line pinned to the panel's foot, so the trade is in the same place for every item. */}
                    <TradeBar itemName={item.name} essence={essence} isTrading={isTrading} onTrade={() => void handleTrade()} className={styles.foot} />
                </>
            )}
        </Panel>
    );
}
